<template>
  <div class="user-menu-page">
    <QHeader class="bg-white text-primary header-ios-safe">
      <HeaderImageTitle :photo="etabProfile.getPhoto()">
        <QToolbar color="transparent">
          <QToolbarTitle v-if="etabProfile.getEtabName()">
            {{ displayTitle }}
          </QToolbarTitle>
        </QToolbar>
      </HeaderImageTitle>
    </QHeader>

    <QPageContainer>
      <QPage>
        <div>
          <UserMenu
            :is-master-etab="isMasterEtab"
            :is-master-etab-without-supervised-etab="isMasterEtabWithoutSupervisedEtab"
            @on-click-etab="onClickEtab"
            @on-click-informations="onClickInformations"
            @on-click-etab-admin-folder="onClickEtabAdminFolder"
            @on-click-subscription="OnClickSubscription"
            @on-click-help="onClickHelp"
            @on-click-logout="onClickLogout"
            @on-click-developer-tools="onClickDeveloperTools"
            @on-click-multi-account="onClickMultiAccount"
            @on-click-documents="onClickDocuments"
          />
        </div>
        <div class="user-menu-footer">
          <div>
            <QBtn flat :label="$t('ETAB.PAGES.DASHBOARD.USER_MENU.@CGU')" @click="onClickCGU" />
            <QBtn flat :label="$t('ETAB.PAGES.DASHBOARD.USER_MENU.@CONFIDENTIALITY')" @click="onClickConfidentiality" />
          </div>
          <div class="q-mt-sm">
            <AppVersion />
          </div>
        </div>
      </QPage>
    </QPageContainer>
  </div>
</template>

<script setup>
  import { reactive, ref, computed, onMounted } from "vue";
  import { LoadingManager } from "@core/managers/loading.manager";
  import { useRouter } from "vue-router";
  import { useI18n } from "vue-i18n";
  import { useQuasar } from "quasar";
  import { useProfileStore } from "@etab/stores/profile";
  import { NotificationsManager } from "@core/managers/notifications.manager";
  import { ContactManager } from "@core/managers/contact.manager";
  import { AuthenticationManager } from "@core/managers/authentication.manager";
  import { JavascriptHelpers } from "@core/helpers";
  import { GETTER_PROFILE_GET } from "@etab/stores/types";
  import { getActivePinia } from "pinia";
  import { ETAB_ROUTES } from "@etab/config";
  import { NEW_FAQ_ETAB_URL, ROLE, HELP_ACTIONS, INTERIM_EMAIL_EXTRACADABRA_SUPPORT } from "@core/constants";
  import { UserStorageManager } from "@core/managers/user-storage.manager";
  import { ScrollManager } from "@core/managers/scroll.manager";
  import { TrackingManager } from "@core/managers/tracking.manager";
  import AppVersion from "@core/components/utils/AppVersion.component.vue";
  import UserMenu from "@etab/components/dashboard/UserMenu.component.vue";
  import HeaderImageTitle from "@etab/components/dashboard/HeaderImageTitle.component.vue";

  const $q = useQuasar();
  const { t } = useI18n();
  const router = useRouter();
  const profileStore = useProfileStore();

  const state = reactive({ hasSupervisedEtab: undefined });
  const isMasterEtab = ref(false);

  const displayTitle = computed(() => {
    return `${etabProfile.value.getEtabName()} - ${etabProfile.value.getCategoryLabel()}`;
  });
  const etabProfile = computed(() => {
    return profileStore[GETTER_PROFILE_GET];
  });

  const isMasterEtabWithoutSupervisedEtab = computed(() => {
    return isMasterEtab.value && !state.hasSupervisedEtab;
  });

  const initRole = async function () {
    const user = await UserStorageManager.getUser();
    if (user.role[0] === ROLE.MASTER) {
      isMasterEtab.value = true;
    }
  };

  const hasCurrentSupervisedEtab = async function () {
    state.hasSupervisedEtab = !!(await UserStorageManager.getSupervisorData());
  };

  const onClickEtab = function () {
    router.push({ name: ETAB_ROUTES.account.profile.info.name }).catch((suppressedError) => {});
  };

  const onClickEtabAdminFolder = function () {
    router.push({ name: ETAB_ROUTES.account.adminFolderStatus.name }).catch((suppressedError) => {});
  };

  const onClickInformations = function () {
    router.push({ name: ETAB_ROUTES.account.accountInformations.name }).catch((suppressedError) => {});
  };

  const OnClickSubscription = function () {
    router.push({ name: ETAB_ROUTES.subscription.menu.name }).catch((suppressedError) => {});
  };

  const onClickDeveloperTools = function () {
    router.push({ name: ETAB_ROUTES.account.developer.name }).catch((suppressedError) => {});
  };

  const onClickDocuments = function () {
    router.push({ name: ETAB_ROUTES.account.documents.root.name }).catch((suppressedError) => {});
  };

  const onClickLogout = async function () {
    try {
      LoadingManager.show();
      // disconnect
      await AuthenticationManager.logout();
      getActivePinia()._s.forEach((store) => store.$reset());

      setTimeout(() => {
        // we reload the full app after logout in order to be sure we reset everything
        JavascriptHelpers.reloadWindow();
      }, 1000);
    } catch (error) {
      NotificationsManager.error({ message: "ERRORS.@SOMETHING_WENT_WRONG" });
      throw error;
    }
  };

  const onClickConfidentiality = function () {
    router.push({ name: ETAB_ROUTES.account.confidentiality.name }).catch((suppressedError) => {});
  };

  const onClickCGU = function () {
    router.push({ name: ETAB_ROUTES.account.cgu.name }).catch((suppressedError) => {});
  };

  const onClickMultiAccount = function () {
    router.push({ name: ETAB_ROUTES.account.multiAccount.name }).catch((suppressedError) => {});
  };

  const onClickHelp = function () {
    const ACTIONS = {
      ASSISTANT: HELP_ACTIONS.ASSISTANT,
      SEND_EMAIL: HELP_ACTIONS.SEND_EMAIL,
      IMPROUVEMENT: HELP_ACTIONS.IMPROUVEMENT,
      OPEN_FAQ: HELP_ACTIONS.OPEN_FAQ,
    };
    $q.bottomSheet({
      message: t("ETAB.PAGES.DASHBOARD.USER_MENU.@NEED_HELP"),
      actions: [
        {
          label: t("ETAB.PAGES.DASHBOARD.USER_MENU.@WRITE_TO_US"),
          icon: "icon-f-mail",
          id: ACTIONS.SEND_EMAIL,
        },
        {
          label: t("ETAB.PAGES.DASHBOARD.USER_MENU.@IMPROVEMENT"),
          icon: "icon-f-mail",
          id: ACTIONS.IMPROUVEMENT,
        },
        {
          label: t("ETAB.PAGES.DASHBOARD.USER_MENU.@HELP"),
          icon: "icon-f-book-open",
          id: ACTIONS.OPEN_FAQ,
        },
        {
          label: t("ETAB.PAGES.DASHBOARD.USER_MENU.@DISCOVER_ASSISTANT"),
          icon: "icon-f-bookmark",
          id: ACTIONS.ASSISTANT,
        },
      ],
    }).onOk((action) => {
      switch (action.id) {
        case ACTIONS.OPEN_FAQ: {
          TrackingManager.addEvent({
            event: "profil_need_help_faq",
          });
          JavascriptHelpers.openUrl(NEW_FAQ_ETAB_URL, "_blank");
          break;
        }
        default:
        case ACTIONS.ASSISTANT: {
          TrackingManager.addEvent({
            event: "profil_need_help_assistant",
          });
          router.push({ name: ETAB_ROUTES.account.onboarding.name });
          break;
        }
        case ACTIONS.SEND_EMAIL: {
          TrackingManager.addEvent({
            event: "profil_need_help_send_email",
          });
          if (etabProfile.value.isInterimEtab()) {
            ContactManager.contactSupport({ email: INTERIM_EMAIL_EXTRACADABRA_SUPPORT });
          } else {
            ContactManager.contactEtabSupport();
          }
          break;
        }
        case ACTIONS.IMPROUVEMENT: {
          TrackingManager.addEvent({
            event: "profil_need_help_improve_app",
          });
          ContactManager.contactEtabSupport({ subject: "EMAILS.@ETAB_IMPROVE_APP" });
          break;
        }
      }
    });
  };

  onMounted(() => {
    initRole();
    hasCurrentSupervisedEtab();
    ScrollManager.scrollToTop();
    // we stop the loading started in beforeRoute
    LoadingManager.hide();
  });
</script>

<style lang="scss">
  .user-menu-page {
    .q-page {
      padding: 15px;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
    }

    h4 {
      color: $primary-lighter;
    }

    .user-menu-component {
      margin-top: 15px;
    }
    .user-menu-footer {
      display: flex;
      justify-content: space-between;
      @media screen and (max-width: $breakpoint-sm) {
        margin-bottom: 15px;
        text-align: center;
        flex-direction: column;
        > div {
          padding: 10px;
        }
      }
    }
  }
</style>
